<template>
  <!-- Docked beside the grid on a wide screen, a sheet everywhere else. -->
  <Teleport to="body" :disabled="docked">
    <div :class="docked ? 'clues-dock' : 'sheet-backdrop'" @click.self="docked || $emit('close')">
      <div :class="docked ? 'clues clues--docked' : 'sheet clues'" :role="docked ? null : 'dialog'" aria-label="All clues">
        <button v-if="!docked" class="sheet__close" aria-label="Close" @click="$emit('close')">✕</button>
        <div ref="cols" class="clues__cols">
          <section v-for="dir in ['across', 'down']" :key="dir">
            <h3>{{ dir === 'across' ? 'Across' : 'Down' }}</h3>
            <ol>
              <li
                v-for="e in puzzle[dir]"
                :key="`${dir}${e.num}`"
                :class="{ 'is-current': isCurrent(e, dir), 'is-cross': isCross(e, dir), 'is-done': isDone(e, dir), 'is-theme': e.theme }"
                @click="go(e, dir)"
              >
                <span class="clues__num">{{ e.num }}</span>
                <span class="clues__text">{{ e.clue }}</span>
              </li>
            </ol>
          </section>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
// Every clue at once - for the people who like to read the whole thing
// first, and for a desktop window where there is room beside the grid.
import { mapState, mapGetters } from 'vuex';
import { cellsOf, letterAt } from '@/engine/solving';

export default {
  name: 'ClueList',
  props: {
    // Beside the grid on a wide screen (Matt, 2026-10-09: "all of the games
    // need to look nicer on desktop and tablet"): always open, and it keeps
    // the clue you're on in view.
    docked: { type: Boolean, default: false },
  },
  emits: ['close'],
  computed: {
    ...mapState(['puzzle', 'letters', 'cursor', 'solvedAt']),
    ...mapGetters({ current: 'currentClue', cross: 'crossClue' }),
  },
  watch: {
    current() { this.keepInView(); },
    cross() { this.keepInView(); },
  },
  methods: {
    // Docked: the crossing clue, then the current one - so if both cannot
    // fit at once, the one you are typing into wins.
    keepInView() {
      if (!this.docked) return;
      this.$nextTick(() => {
        ['.is-cross', '.is-current'].forEach((sel) => {
          this.$refs.cols?.querySelector(sel)?.scrollIntoView({ block: 'nearest' });
        });
      });
    },
    // A solved puzzle has no current or crossing clue: the reveal's gold is
    // the theme's alone.
    isCurrent(e, dir) { return !this.solvedAt && this.cursor.dir === dir && this.current === e; },
    // The crossing clue: the other direction's entry through the cursor square.
    isCross(e, dir) { return !this.solvedAt && this.cursor.dir !== dir && this.cross === e; },
    isDone(e, dir) { return cellsOf(e, dir).every(([r, c]) => letterAt(this.letters, r, c)); },
    go(e, dir) {
      this.$store.dispatch('goToClue', { entry: e, dir });
      if (!this.docked) this.$emit('close');
    },
  },
};
</script>

<style lang="scss" scoped>
.clues { max-width: 720px; }
.clues-dock { min-height: 0; display: flex; }
.clues--docked {
  flex: 1; min-height: 0; overflow-y: auto; overscroll-behavior: contain;
  padding: 0.9rem 1rem; border: 1px solid var(--edge); border-radius: var(--radius);
  background: rgba(0, 0, 0, 0.15);
  .clues__cols { grid-template-columns: 1fr 1fr; }
  li { font-size: 0.95rem; }
}
.clues__cols {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  @media (min-width: 560px) { grid-template-columns: 1fr 1fr; }
}
h3 { margin: 0 0 0.4rem; font-size: 0.8rem; letter-spacing: 0.08em; text-transform: uppercase; color: var(--ink-dim); }
ol { list-style: none; margin: 0; padding: 0; }
li {
  display: flex; gap: 0.5rem; padding: 0.3rem 0.4rem; border-radius: 6px; cursor: pointer; font-size: 0.88rem; line-height: 1.3;
  &.is-current { background: var(--accent); color: #211a05; }
  &.is-cross { background: rgba(255, 255, 255, 0.1); box-shadow: inset 3px 0 0 var(--accent); }
  &.is-done:not(.is-current) { color: var(--ink-dim); }
  &.is-theme .clues__num::after { content: '*'; }
}
.clues__num { flex: 0 0 2rem; font-weight: 700; text-align: right; }
</style>
